import { useSessionStore, type BranchAccess } from '../../../shell/session/store/useSessionStore';
import {
  applyBranchAccessPatch,
  type BranchAccessPatch,
} from '../../../settings/screens/branchSettingsScreen.utils';
import { getCachedBranches } from './branchesCache';
import { parseStockSalePolicy, parseBarcodeInputMode, parseScaleBarcodeProfile, parseKitchenFireMode } from '../../application/authBranches';

/** Actualiza la sucursal en el store de sesión leyendo el estado más reciente (evita closures stale). */
export function patchSessionBranch(branchId: string, patch: BranchAccessPatch): void {
  const { branches, setBranches } = useSessionStore.getState();
  setBranches(applyBranchAccessPatch(branches, branchId, patch));
}

/** Aplica settings de sucursal desde SQLite (`branches_cache`) al store de sesión (offline-first). */
export async function syncBranchSettingsFromLocalCacheToSession(): Promise<void> {
  const { branches, setBranches } = useSessionStore.getState();
  if (branches.length === 0) return;

  let cached: Awaited<ReturnType<typeof getCachedBranches>>;
  try {
    cached = await getCachedBranches({ ttl: Number.MAX_SAFE_INTEGER });
  } catch {
    return;
  }
  if (cached.length === 0) return;

  const byId = new Map(cached.map((entry) => [entry.id, entry]));
  let changed = false;
  const next: BranchAccess[] = branches.map((branch) => {
    const entry = byId.get(branch.id);
    if (!entry) return branch;

    const stockSalePolicy = parseStockSalePolicy(entry.stock_sale_policy);
    const barcodeInputMode = parseBarcodeInputMode(entry.barcode_input_mode);
    const scaleBarcodeProfile = parseScaleBarcodeProfile(entry.scale_barcode_profile);
    const manualDiscountMaxPercent = entry.manual_discount_max_percent ?? 20;
    const manualDiscountMaxAmount = entry.manual_discount_max_amount ?? 5000;
    const consolidateCartLines = entry.consolidate_cart_lines !== false;
    const kitchenFireMode = parseKitchenFireMode(entry.kitchen_fire_mode);
    const name = entry.name;
    const location = entry.address ?? 'Dirección no cargada';

    if (
      branch.name === name &&
      branch.location === location &&
      branch.stockSalePolicy === stockSalePolicy &&
      branch.barcodeInputMode === barcodeInputMode &&
      branch.scaleBarcodeProfile === scaleBarcodeProfile &&
      branch.manualDiscountMaxPercent === manualDiscountMaxPercent &&
      branch.manualDiscountMaxAmount === manualDiscountMaxAmount &&
      branch.consolidateCartLines === consolidateCartLines &&
      branch.kitchenFireMode === kitchenFireMode
    ) {
      return branch;
    }

    changed = true;
    return {
      ...branch,
      name,
      location,
      stockSalePolicy,
      barcodeInputMode,
      scaleBarcodeProfile,
      manualDiscountMaxPercent,
      manualDiscountMaxAmount,
      consolidateCartLines,
      kitchenFireMode,
    };
  });

  if (changed) {
    setBranches(next);
  }
}
